import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { toast } from "sonner";
import { Trash2, Plus, AlertCircle, Check, Loader } from "lucide-react";
import api from "../api/axios";
import { useLanguage } from "../context/LanguageContext";

// Updated translations for account-wide context
const domainTranslations = {
  tr: {
    widgetDomainManagement: "Proje Alan Adı Yönetimi",
    addDomainsDescription: "Bu projede widget'ınızın çalışacağı alan adları",
    planInfo: "Hesap Plan Bilgileri",
    domainUsage: "hesap genelinde kullanılıyor", // Changed
    addNewDomain: "Yeni Alan Adı Ekle",
    domainPlaceholder: "example.com veya *.example.com",
    add: "Ekle",
    wildcardHint: "Tüm alt alan adlarına izin vermek için wildcard kullanın: *.example.com",
    allowedDomains: "Bu Projedeki İzinli Alan Adları", // Changed
    noDomains: "Bu projeye henüz alan adı eklenmedi.", // Changed
    upgradePlan: "Daha fazla alan adı eklemek için hesap planınızı yükseltin", // Changed
    invalidDomain: "Geçersiz alan adı. Örnek: example.com, *.example.com",
    domainEmpty: "Alan adı boş olamaz",
    domainExists: "Bu alan adı zaten bu projede mevcut", // Changed
    domainAdded: "Alan adı başarıyla eklendi",
    domainRemoved: "Alan adı kaldırıldı",
    domainAddFailed: "Alan adı eklenemedi",
    domainRemoveFailed: "Alan adı kaldırılamadı",
    confirm: "Onayla",
    cancel: "İptal",
    removeConfirm: "Bu alan adını kaldırmak istediğinizden emin misiniz?",
    limitReached: "Hesap planınız için maksimum alan adı limitine ulaştınız", // Changed
    projectDomainLimit: "Bu projede zaten bir alan adı var. Her projeye sadece 1 alan adı ekleyebilirsiniz.",
    replaceDomain: "Alan adını değiştirmek için mevcut alan adını silip yeni bir tane ekleyin",
    free: "Free",
    premium: "Premium",
    enterprise: "Enterprise",
    loading: "Yükleniyor...",
  },
  en: {
    widgetDomainManagement: "Project Domain Management",
    addDomainsDescription: "Domains where your widget will be active for this project",
    planInfo: "Account Plan Information",
    domainUsage: "domains used account-wide", // Changed
    addNewDomain: "Add New Domain",
    domainPlaceholder: "example.com or *.example.com",
    add: "Add",
    wildcardHint: "Use wildcard: *.example.com to allow all subdomains",
    allowedDomains: "Allowed Domains in This Project", // Changed
    noDomains: "No domains added to this project yet.", // Changed
    upgradePlan: "Upgrade your account plan to add more domains", // Changed
    invalidDomain: "Invalid domain format. Examples: example.com, *.example.com",
    domainEmpty: "Domain cannot be empty",
    domainExists: "Domain already exists in this project", // Changed
    domainAdded: "Domain added successfully",
    domainRemoved: "Domain removed",
    domainAddFailed: "Failed to add domain",
    domainRemoveFailed: "Failed to remove domain",
    confirm: "Confirm",
    cancel: "Cancel",
    removeConfirm: "Are you sure you want to remove this domain?",
    limitReached: "Maximum domains reached for your account plan", // Changed
    projectDomainLimit: "This project already has a domain. Each project can only have 1 domain.",
    replaceDomain: "To change the domain, remove the existing domain and add a new one.",
    free: "Free",
    premium: "Premium",
    enterprise: "Enterprise",
    loading: "Loading...",
  },
  de: {
    widgetDomainManagement: "Projekt-Domänenverwaltung",
    addDomainsDescription: "Fügen Sie Domains hinzu, auf denen Ihr Widget für dieses Projekt aktiv sein wird",
    planInfo: "Kontoplan-Informationen",
    domainUsage: "Domains kontoübergreifend verwendet",
    addNewDomain: "Neue Domain hinzufügen",
    domainPlaceholder: "example.com oder *.example.com",
    add: "Hinzufügen",
    wildcardHint: "Verwenden Sie Wildcard: *.example.com, um alle Subdomains zuzulassen",
    allowedDomains: "Zulässige Domains in diesem Projekt",
    noDomains: "Noch keine Domains zu diesem Projekt hinzugefügt.",
    upgradePlan: "Upgraden Sie Ihren Kontoplan, um weitere Domains hinzuzufügen",
    invalidDomain: "Ungültiges Domänenformat. Beispiele: example.com, *.example.com",
    domainEmpty: "Domain kann nicht leer sein",
    domainExists: "Domain existiert bereits in diesem Projekt",
    domainAdded: "Domain erfolgreich hinzugefügt",
    domainRemoved: "Domain entfernt",
    domainAddFailed: "Domain konnte nicht hinzugefügt werden",
    domainRemoveFailed: "Domain konnte nicht entfernt werden",
    confirm: "Bestätigen",
    cancel: "Abbrechen",
    removeConfirm: "Möchten Sie diese Domain wirklich entfernen?",
    limitReached: "Maximale Anzahl von Domains für Ihren Kontoplan erreicht",
    projectDomainLimit: "Dieses Projekt hat bereits eine Domain. Jedes Projekt kann nur 1 Domain haben.",
    replaceDomain: "Um die Domain zu ändern, entfernen Sie die vorhandene Domain und fügen Sie eine neue hinzu.",
    free: "Kostenlos",
    premium: "Premium",
    enterprise: "Enterprise",
    loading: "Lädt...",
  },
  es: {
    widgetDomainManagement: "Gestión de Dominio del Proyecto",
    addDomainsDescription: "Agregue dominios donde su widget estará activo para este proyecto",
    planInfo: "Información del Plan de Cuenta",
    domainUsage: "dominios utilizados en toda la cuenta",
    addNewDomain: "Agregar Nuevo Dominio",
    domainPlaceholder: "example.com o *.example.com",
    add: "Agregar",
    wildcardHint: "Use comodín: *.example.com para permitir todos los subdominios",
    allowedDomains: "Dominios Permitidos en este Proyecto",
    noDomains: "Aún no se han agregado dominios a este proyecto.",
    upgradePlan: "Actualice el plan de su cuenta para agregar más dominios",
    invalidDomain: "Formato de dominio inválido. Ejemplos: example.com, *.example.com",
    domainEmpty: "El dominio no puede estar vacío",
    domainExists: "El dominio ya existe en este proyecto",
    domainAdded: "Dominio agregado exitosamente",
    domainRemoved: "Dominio eliminado",
    domainAddFailed: "No se pudo agregar el dominio",
    domainRemoveFailed: "No se pudo eliminar el dominio",
    confirm: "Confirmar",
    cancel: "Cancelar",
    removeConfirm: "¿Está seguro de que desea eliminar este dominio?",
    limitReached: "Se alcanzó el número máximo de dominios para el plan de su cuenta",
    projectDomainLimit: "Este proyecto ya tiene un dominio. Cada proyecto solo puede tener 1 dominio.",
    replaceDomain: "Para cambiar el dominio, elimine el dominio existente y agregue uno nuevo.",
    free: "Gratuito",
    premium: "Premium",
    enterprise: "Empresarial",
    loading: "Cargando...",
  },
  fr: {
    widgetDomainManagement: "Gestion des Domaines du Projet",
    addDomainsDescription: "Ajoutez les domaines où votre widget sera actif pour ce projet",
    planInfo: "Informations du Plan de Compte",
    domainUsage: "domaines utilisés sur l'ensemble du compte",
    addNewDomain: "Ajouter un Nouveau Domaine",
    domainPlaceholder: "example.com ou *.example.com",
    add: "Ajouter",
    wildcardHint: "Utilisez le wildcard: *.example.com pour autoriser tous les sous-domaines",
    allowedDomains: "Domaines Autorisés dans ce Projet",
    noDomains: "Aucun domaine ajouté à ce projet pour le moment.",
    upgradePlan: "Mettez à niveau le plan de votre compte pour ajouter plus de domaines",
    invalidDomain: "Format de domaine invalide. Exemples: example.com, *.example.com",
    domainEmpty: "Le domaine ne peut pas être vide",
    domainExists: "Le domaine existe déjà dans ce projet",
    domainAdded: "Domaine ajouté avec succès",
    domainRemoved: "Domaine supprimé",
    domainAddFailed: "Impossible d'ajouter le domaine",
    domainRemoveFailed: "Impossible de supprimer le domaine",
    confirm: "Confirmer",
    cancel: "Annuler",
    removeConfirm: "Êtes-vous sûr de vouloir supprimer ce domaine?",
    limitReached: "Nombre maximum de domaines atteint pour le plan de votre compte",
    projectDomainLimit: "Ce projet a déjà un domaine. Chaque projet ne peut avoir que 1 domaine.",
    replaceDomain: "Pour changer le domaine, supprimez le domaine existant et ajoutez-en un nouveau.",
    free: "Gratuit",
    premium: "Premium",
    enterprise: "Entreprise",
    loading: "Chargement...",
  }
};

// The component no longer needs planType as a prop, as it's fetched from the API.
export const DomainManagement = ({ tenantId }) => {
  // State for domains in the current project
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // State for account-wide plan and limits
  const [maxDomains, setMaxDomains] = useState(1);
  const [totalDomainsInAccount, setTotalDomainsInAccount] = useState(0);
  const [currentPlanType, setCurrentPlanType] = useState("free");

  const { language } = useLanguage();
  const t = domainTranslations[language] || domainTranslations.tr;

  const getPlanInfo = () => {
    const plans = {
      free: { name: t.free, color: "bg-gray-100 dark:bg-gray-800", textColor: "text-gray-900 dark:text-gray-300" },
      premium: { name: t.premium, color: "bg-blue-100 dark:bg-blue-900/20", textColor: "text-blue-900 dark:text-blue-300" },
      enterprise: { name: t.enterprise, color: "bg-purple-100 dark:bg-purple-900/20", textColor: "text-purple-900 dark:text-purple-300" }
    };
    return plans[currentPlanType] || plans.free;
  };

  // Load domains and account-wide limits
  useEffect(() => {
    loadDomains();
  }, [tenantId]);

  const loadDomains = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      const response = await api.get(`/tenants/${tenantId}/domains`);
      // Domains for this specific project
      setDomains(response.data.allowed_domains || []);
      // Account-wide data
      setMaxDomains(
        response.data.max_domains === null || response.data.max_domains === undefined
          ? Number.POSITIVE_INFINITY
          : response.data.max_domains
      );
      setTotalDomainsInAccount(response.data.total_domains_in_account || 0);
      setCurrentPlanType(response.data.plan_type || "free");
    } catch (error) {
      console.error("Failed to load domains:", error);
      toast.error("Failed to load domain information.");
    } finally {
      setLoading(false);
    }
  };

  const validateDomain = (domain) => {
    const regex = /^[\w\-\.]+\.\w{2,}$|^\*\.[\w\-\.]+\.\w{2,}$/;
    return regex.test(domain);
  };

  const handleAddDomain = async (e) => {
    e?.preventDefault();

    const domainTrimmed = newDomain.trim().toLowerCase();

    if (!domainTrimmed) {
      toast.error(t.domainEmpty);
      return;
    }

    if (!validateDomain(domainTrimmed)) {
      toast.error(t.invalidDomain);
      return;
    }

    if (domains.includes(domainTrimmed)) {
      toast.error(t.domainExists);
      return;
    }

    // Check against account-wide limit
    if (Number.isFinite(maxDomains) && totalDomainsInAccount >= maxDomains) {
      toast.error(t.limitReached);
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.post(`/tenants/${tenantId}/domains`, {
        domain: domainTrimmed
      });

      // The API returns the updated list for the current project
      setDomains(response.data.allowed_domains || []);
      // Manually update the total count for immediate UI feedback
      setTotalDomainsInAccount(prev => prev + 1);
      setNewDomain("");
      toast.success(t.domainAdded);
    } catch (error) {
      const message = error.response?.data?.detail || t.domainAddFailed;
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDomain = async (domain) => {
    setIsSaving(true);
    try {
      const response = await api.delete(
        `/tenants/${tenantId}/domains/${encodeURIComponent(domain)}`
      );

      setDomains(response.data.allowed_domains || []);
      // Manually update the total count for immediate UI feedback
      setTotalDomainsInAccount(prev => prev - 1);
      setShowDeleteConfirm(null);
      toast.success(t.domainRemoved);
    } catch (error) {
      const message = error.response?.data?.detail || t.domainRemoveFailed;
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const plan = getPlanInfo();
  const maxDomainsLabel = Number.isFinite(maxDomains) ? maxDomains : "∞";
  const hasDomainQuota = !Number.isFinite(maxDomains) || totalDomainsInAccount < maxDomains;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.widgetDomainManagement}</CardTitle>
        <CardDescription>{t.addDomainsDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Plan Info - Now shows account-wide usage */}
        <div className={`p-4 rounded-lg ${plan.color}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`font-semibold ${plan.textColor}`}>{plan.name} Plan</h3>
              <p className={`text-sm ${plan.textColor} opacity-75`}>
                {totalDomainsInAccount} / {maxDomainsLabel} {t.domainUsage}
              </p>
            </div>
          </div>
        </div>

        {/* Add Domain Form - Only show if account has quota AND project has no domain yet */}
        {hasDomainQuota ? (
          domains.length === 0 ? (
            <form onSubmit={handleAddDomain} className="space-y-3 border-t pt-4">
              <Label htmlFor="domain">{t.addNewDomain}</Label>
              <div className="flex gap-2">
                <Input
                  id="domain"
                  placeholder={t.domainPlaceholder}
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  disabled={isSaving}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  disabled={isSaving || !newDomain.trim()}
                  className="gap-2"
                >
                  {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {t.add}
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t.wildcardHint}</p>
            </form>
          ) : (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <div className="font-semibold">{t.projectDomainLimit}</div>
                <div className="text-sm mt-1">{t.replaceDomain}</div>
              </AlertDescription>
            </Alert>
          )
        ) : (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {t.limitReached}
            </AlertDescription>
          </Alert>
        )}

        {/* Domain List for the current project */}
        {domains.length > 0 ? (
          <div className="space-y-3 border-t pt-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">{t.allowedDomains}</h3>
            <div className="space-y-2">
              {domains.map((domain) => (
                <div
                  key={domain}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="font-mono text-sm text-gray-900 dark:text-gray-300">{domain}</span>
                  </div>
                  <div className="flex gap-2">
                    {showDeleteConfirm === domain ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowDeleteConfirm(null)}
                          disabled={isSaving}
                          className="text-gray-600 dark:text-gray-400"
                        >
                          {t.cancel}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteDomain(domain)}
                          disabled={isSaving}
                        >
                          {isSaving ? <Loader className="w-3 h-3 animate-spin" /> : t.confirm}
                        </Button>
                      </>
                    ) : (
                      <button
                        onClick={() => setShowDeleteConfirm(domain)}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        disabled={isSaving}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {showDeleteConfirm && (
                <div className="text-xs text-gray-500 p-2">
                  {t.removeConfirm}
                </div>
              )}
            </div>
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t.noDomains}</AlertDescription>
          </Alert>
        )}

        {/* Plan Upgrade Notice - Shown when account limit is reached */}
        {Number.isFinite(maxDomains) && totalDomainsInAccount >= maxDomains && maxDomains < 10 && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800 flex items-center justify-between">
              <span>{t.upgradePlan}</span>
              {/* An upgrade button could be added here, linking to the account plan page */}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
